import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { pseoAPI } from '../utils/api';
import { SEO } from '../components/SEO';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Newsletter } from '../components/Newsletter';
import { useRegion } from '../context/RegionContext';
import { Gift, Calendar, ArrowRight, Percent, Clock } from 'lucide-react';

/**
 * Sale Events Listing Page
 * Shows all active sale events (Black Friday, 11.11, etc.)
 */
export const SaleEventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentRegion } = useRegion();

  const buildUrl = (path) => {
    if (currentRegion === 'global') return path;
    return `/${currentRegion}${path}`;
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await pseoAPI.getSaleEvents(true);
        setEvents(response.data);
      } catch (error) {
        console.error('Failed to fetch sale events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brand-red mx-auto mb-4"></div>
          <p className="text-gray-600 font-inter">Loading sales...</p>
        </div>
      </div>
    );
  }

  // Predefined sale events for when DB is empty
  const defaultEvents = [
    {
      slug: 'black-friday-deals',
      name: 'Black Friday Deals',
      short_description: 'Massive discounts across all categories',
      banner_image: 'https://images.unsplash.com/photo-1607082350899-7e105aa886ae?w=800',
      is_active: true
    },
    {
      slug: 'cyber-monday-deals',
      name: 'Cyber Monday Deals',
      short_description: 'Online-exclusive tech and electronics deals',
      banner_image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800',
      is_active: true
    },
    {
      slug: '11-11-sale',
      name: '11.11 Singles Day Sale',
      short_description: 'The biggest online shopping day of the year',
      banner_image: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=800',
      is_active: true
    },
    {
      slug: '12-12-sale',
      name: '12.12 Birthday Sale',
      short_description: 'Year-end mega sale event',
      banner_image: 'https://images.unsplash.com/photo-1513885535751-8b9238bd345a?w=800',
      is_active: true
    },
    {
      slug: 'christmas-deals',
      name: 'Christmas Deals',
      short_description: 'Holiday shopping discounts and gift ideas',
      banner_image: 'https://images.unsplash.com/photo-1482517967863-00e15c9b44be?w=800',
      is_active: true
    }
  ];

  const displayEvents = events.length > 0 ? events : defaultEvents;

  return (
    <>
      <SEO 
        title="Sale Events - Best Deals & Discounts"
        description="Find the best deals during major sale events. Black Friday, Cyber Monday, 11.11, 12.12, and more shopping events with exclusive discounts."
        type="website"
      />

      <div className="min-h-screen bg-gray-50" data-testid="sale-events-page">
        <Breadcrumbs items={[]} currentPage="Sale Events" />

        {/* Hero */}
        <div className="bg-gradient-to-r from-brand-red to-red-700 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <Gift className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-playfair font-bold">Sale Events</h1>
                <p className="text-red-100 text-lg mt-1">Don't miss out on the biggest shopping events</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Events Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayEvents.map((event) => (
              <Link
                key={event.slug}
                to={buildUrl(`/sale-events/${event.slug}`)}
                className="group bg-white rounded-2xl overflow-hidden hover:shadow-xl smooth-transition border border-gray-100"
                data-testid={`event-card-${event.slug}`}
              >
                {/* Banner Image */}
                <div className="aspect-[16/9] relative overflow-hidden">
                  <img
                    src={event.banner_image || 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=800'}
                    alt={event.name}
                    className="w-full h-full object-cover group-hover:scale-105 smooth-transition duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h2 className="text-2xl font-playfair font-bold text-white mb-1 group-hover:text-brand-yellow smooth-transition">
                      {event.name}
                    </h2>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {event.short_description || event.description?.slice(0, 100)}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {event.start_date && (
                        <>
                          <Calendar className="w-4 h-4" />
                          <span>{event.start_date}</span>
                        </>
                      )}
                    </div>
                    <span className="inline-flex items-center gap-1 text-brand-red font-semibold text-sm group-hover:gap-2 smooth-transition">
                      View Deals <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Info Section */}
          <div className="mt-16 bg-white rounded-2xl p-8 border border-gray-100">
            <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">
              About Shopping Events
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-brand-yellow/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Percent className="w-6 h-6 text-brand-yellow" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Massive Discounts</h3>
                  <p className="text-sm text-gray-600">Save up to 90% during major sale events</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-brand-red/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-brand-red" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Limited Time</h3>
                  <p className="text-sm text-gray-600">Flash deals that won't last long</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Gift className="w-6 h-6 text-brand-green" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Free Gifts</h3>
                  <p className="text-sm text-gray-600">Bonus items and freebies with purchases</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Newsletter />
      </div>
    </>
  );
};

export default SaleEventsPage;
